import { gql, useQuery } from '@apollo/client'
import { Separator, Spinner } from '@farol-ds/react'
import { nullableConnectionNodes } from '@jusbrasil-web/shared-apollo-client/utilities'
import React, { useEffect, useMemo, useState } from 'react'

import { Comment } from '../../comment/comment'
import {
  CommentList_DocumentFragment,
  CommentList_ViewerFragment,
  LoadMoreCommentsByDocumentQuery,
} from './__generated__/commentList.graphql'
import styles from './styles.module.scss'

export interface CommentListProps {
  document: CommentList_DocumentFragment
  viewer: CommentList_ViewerFragment | null
  onLoadMore?: () => void
}

export const LOAD_MORE_COMMENTS = gql`
  query LoadMoreCommentsByDocument(
    $docId: NumericID!
    $artifact: String!
    $limit: Int!
    $after: String
  ) {
    root {
      documentByNumericID(artifact: $artifact, docId: $docId) {
        id
        comments(first: $limit, after: $after) {
          edges {
            cursor
            node {
              ...Comment_comment
            }
          }
        }
      }
    }
  }

  ${Comment.fragments.comment}
`

export function CommentList(props: CommentListProps) {
  const { document, viewer, onLoadMore } = props
  const { docId, artifact } = document
  const { data, fetchMore, loading } = useQuery<LoadMoreCommentsByDocumentQuery>(
    LOAD_MORE_COMMENTS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        docId,
        artifact,
        after: null,
        limit: 4,
      },
    }
  )

  const comments = nullableConnectionNodes(data?.root.documentByNumericID?.comments)
  const lastCommentIndex = useMemo(() => {
    const lastValidCommentIndex = data?.root.documentByNumericID?.comments?.edges?.findIndex(
      (item) => item?.cursor === ''
    ) as number
    return lastValidCommentIndex !== -1 ? lastValidCommentIndex - 1 : comments.length - 1
  }, [comments.length, data?.root.documentByNumericID?.comments?.edges])
  const [lastItem, setLastItem] = useState<Element | null>(null)
  const [lastComment, setLastComment] = useState(comments[lastCommentIndex])

  useEffect(() => {
    if (lastComment === comments.at(lastCommentIndex)) return

    const currentObserver = new IntersectionObserver((entries) => {
      const first = entries[0]
      if (first.isIntersecting) {
        fetchMore({
          variables: {
            after: data?.root.documentByNumericID?.comments?.edges?.at(lastCommentIndex)?.cursor,
          },
        }).then(() => {
          setLastComment(comments[lastCommentIndex])
          onLoadMore?.()
        })
      }
    })

    const currentElement = lastItem

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [lastItem, lastComment, lastCommentIndex, fetchMore, comments, data, onLoadMore])

  return (
    <div className={styles.commentList}>
      {comments.map((comment, idx) => (
        <React.Fragment key={idx}>
          <div ref={lastCommentIndex == idx ? setLastItem : undefined}>
            <Comment key={comment.id} comment={comment} document={document} viewer={viewer} />
          </div>
          {idx < comments.length - 1 && <Separator />}
        </React.Fragment>
      ))}
      {loading && <Spinner className={styles.commentListLoading} />}
    </div>
  )
}

CommentList.fragments = {
  document: gql`
    fragment CommentList_document on Document {
      docId
      artifact
      numComments
      ...Comment_document
    }
    ${Comment.fragments.document}
  `,
  viewer: gql`
    fragment CommentList_viewer on Profile {
      ...Comment_viewer
    }
    ${Comment.fragments.viewer}
  `,
}
